/**
 * Relationships Controller Module
 * Handles Relationships page initialization and data loading
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only relationships page logic
 * - Template Method: Uses BaseController hooks
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';

/**
 * Metadata can be array of [key, value] tuples (old format) or object (new format from proxy)
 */
type MetadataInput = Array<[string, string]> | Record<string, string>;

/**
 * Relationship data structure (raw from canister/proxy)
 */
interface RawRelationship {
    relationship_id: string;
    device_id: string;
    application_id: string;
    relationship_type: string;
    metadata: MetadataInput;
    created_at: number;
    updated_at: number;
}

/**
 * Enriched relationship for display
 */
interface EnrichedRelationship {
    relationship_id: string;
    device_id: string;
    application_id: string;
    relationship_type: string;
    metadata: Record<string, string>;
    created_at: number;
    updated_at: number;
    // Enriched fields from application data
    app_name?: string;
    app_type?: string;
    app_status?: string;
    app_hostname?: string;
    source_tier?: string;
    target_tier?: string;
    service_name?: string;
}

/**
 * RelationshipsController class
 * Manages the relationships page
 */
export class RelationshipsController extends BaseController {
    private allRelationships: EnrichedRelationship[] = [];
    private applicationsMap: Map<string, Record<string, string>> = new Map();

    constructor(services: ControllerServices) {
        super(services);
    }

    getPageName(): PageName {
        return 'relationships';
    }

    protected async doInit(): Promise<void> {
        // Load applications first to enrich relationships
        await this.loadApplicationsData();
        await this.loadRelationshipsData();
        this.setupFilterListeners();
    }

    /**
     * Convert metadata to object
     * Handles both array format [[key, value], ...] and object format {key: value}
     */
    private parseMetadata(metadata: MetadataInput | undefined): Record<string, string> {
        if (!metadata) return {};

        // If it's already an object (new proxy format)
        if (!Array.isArray(metadata)) {
            return metadata as Record<string, string>;
        }

        // Array format (old Candid format)
        const result: Record<string, string> = {};
        for (const item of metadata) {
            if (Array.isArray(item) && item.length >= 2) {
                result[item[0]] = item[1];
            }
        }
        return result;
    }

    /**
     * Load applications data for enrichment
     */
    private async loadApplicationsData(): Promise<void> {
        console.log('Loading applications data for enrichment...');

        if (!this.canisterClient) {
            console.error('CanisterClient not available');
            return;
        }

        try {
            const result = await this.canisterClient.callCanister('vector_db', 'get_all_applications', []);
            console.log('Applications result:', result);

            if (result && Array.isArray(result)) {
                // Result is array of [application_id, metadata_array]
                for (const [appId, metadataArray] of result) {
                    const metadata = this.parseMetadata(metadataArray);
                    this.applicationsMap.set(appId, metadata);
                }
                console.log(`Loaded ${this.applicationsMap.size} applications for enrichment`);
            }
        } catch (error) {
            console.error('Error loading applications:', error);
        }
    }

    /**
     * Enrich relationship with application data
     */
    private enrichRelationship(raw: RawRelationship): EnrichedRelationship {
        const metadata = this.parseMetadata(raw.metadata);

        // Get application metadata
        const appMetadata = this.applicationsMap.get(raw.application_id) || {};

        return {
            relationship_id: raw.relationship_id,
            device_id: raw.device_id,
            application_id: raw.application_id,
            relationship_type: raw.relationship_type,
            metadata: metadata,
            created_at: raw.created_at,
            updated_at: raw.updated_at,
            // Extract enriched fields (app_type is the correct field name from service_map_ops)
            app_name: appMetadata.name || appMetadata.application_name || raw.application_id,
            app_type: appMetadata.app_type || appMetadata.application_type || appMetadata.type || '-',
            app_status: appMetadata.status || 'unknown',
            app_hostname: appMetadata.hostname || appMetadata.host || appMetadata.ip_address || '-',
            source_tier: metadata.source_tier || '-',
            target_tier: metadata.target_tier || '-',
            service_name: metadata.service_name || appMetadata.service_name || '-',
        };
    }

    /**
     * Setup filter event listeners
     */
    private setupFilterListeners(): void {
        const applyBtn = document.getElementById('apply-relationship-filters');
        const resetBtn = document.getElementById('reset-relationship-filters');
        const searchInput = document.getElementById('relationship-search') as HTMLInputElement;

        if (applyBtn) {
            applyBtn.addEventListener('click', () => this.applyFilters());
        }

        if (resetBtn) {
            resetBtn.addEventListener('click', () => this.resetFilters());
        }

        if (searchInput) {
            searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.applyFilters();
                }
            });
        }

        console.log('Relationship filter listeners setup complete');
    }

    /**
     * Apply filters to relationships list
     */
    private applyFilters(): void {
        const searchInput = document.getElementById('relationship-search') as HTMLInputElement;
        const typeFilter = document.getElementById('relationship-type-filter') as HTMLSelectElement;

        const searchTerm = searchInput?.value?.toLowerCase() || '';
        const typeValue = typeFilter?.value || '';

        console.log(`Applying filters: search="${searchTerm}", type="${typeValue}"`);

        let filtered = this.allRelationships;

        // Search filter - now includes enriched fields
        if (searchTerm) {
            filtered = filtered.filter(rel => {
                const deviceId = (rel.device_id || '').toLowerCase();
                const appId = (rel.application_id || '').toLowerCase();
                const appName = (rel.app_name || '').toLowerCase();
                const appType = (rel.app_type || '').toLowerCase();
                const relType = (rel.relationship_type || '').toLowerCase();
                const sourceTier = (rel.source_tier || '').toLowerCase();
                const targetTier = (rel.target_tier || '').toLowerCase();
                const serviceName = (rel.service_name || '').toLowerCase();

                return deviceId.includes(searchTerm) ||
                       appId.includes(searchTerm) ||
                       appName.includes(searchTerm) ||
                       appType.includes(searchTerm) ||
                       relType.includes(searchTerm) ||
                       sourceTier.includes(searchTerm) ||
                       targetTier.includes(searchTerm) ||
                       serviceName.includes(searchTerm);
            });
        }

        // Type filter
        if (typeValue) {
            filtered = filtered.filter(rel => rel.relationship_type === typeValue);
        }

        console.log(`Filtered to ${filtered.length} relationships`);
        this.populateRelationshipsTable(filtered);
        this.updatePaginationInfo(filtered.length);
    }

    /**
     * Reset all filters
     */
    private resetFilters(): void {
        const searchInput = document.getElementById('relationship-search') as HTMLInputElement;
        const typeFilter = document.getElementById('relationship-type-filter') as HTMLSelectElement;
        const envFilter = document.getElementById('relationship-env-filter') as HTMLSelectElement;

        if (searchInput) searchInput.value = '';
        if (typeFilter) typeFilter.value = '';
        if (envFilter) envFilter.value = '';

        console.log('Relationship filters reset');
        this.populateRelationshipsTable(this.allRelationships);
        this.updatePaginationInfo(this.allRelationships.length);
    }

    /**
     * Update pagination info text
     */
    private updatePaginationInfo(count: number): void {
        const infoEl = document.getElementById('relationships-pagination-info');
        if (infoEl) {
            infoEl.textContent = `Showing 1-${count} of ${count}`;
        }
    }

    /**
     * Load relationships data from vector_db
     */
    public async loadRelationshipsData(): Promise<void> {
        console.log('Loading relationships data from vector_db...');

        if (!this.canisterClient) {
            console.error('CanisterClient not available');
            return;
        }

        try {
            const result = await this.canisterClient.callCanister('vector_db', 'get_all_relationships', []);
            console.log('Relationships result:', result);

            if (result && Array.isArray(result)) {
                // Enrich each relationship with application data and parsed metadata
                this.allRelationships = result.map((raw: RawRelationship) => this.enrichRelationship(raw));
                console.log(`Loaded and enriched ${this.allRelationships.length} relationships`);

                this.updateRelationshipsStats(this.allRelationships);
                this.populateRelationshipsTable(this.allRelationships);
            }
        } catch (error) {
            console.error('Error loading relationships:', error);
        }
    }

    /**
     * Update relationships statistics
     */
    private updateRelationshipsStats(relationships: EnrichedRelationship[]): void {
        const total = relationships.length;

        // Count unique devices
        const uniqueDevices = new Set(relationships.map(r => r.device_id));
        const devicesWithApps = uniqueDevices.size;

        // Count unique applications
        const uniqueApps = new Set(relationships.map(r => r.application_id));
        const appsDeployed = uniqueApps.size;

        this.updateElementText('total-relationships-count', total);
        this.updateElementText('devices-with-apps-count', devicesWithApps);
        this.updateElementText('apps-deployed-count', appsDeployed);

        console.log(`Updated relationship stats: ${total} total, ${devicesWithApps} devices, ${appsDeployed} apps`);
    }

    /**
     * Get status badge class based on status value
     */
    private getStatusClass(status: string): string {
        const statusLower = status.toLowerCase();
        if (statusLower === 'running' || statusLower === 'online' || statusLower === 'active') {
            return 'bg-green-100 text-green-800';
        } else if (statusLower === 'stopped' || statusLower === 'offline') {
            return 'bg-red-100 text-red-800';
        } else if (statusLower === 'warning' || statusLower === 'degraded') {
            return 'bg-yellow-100 text-yellow-800';
        }
        return 'bg-gray-100 text-gray-800';
    }

    /**
     * Populate the relationships table with enriched data
     */
    private populateRelationshipsTable(relationships: EnrichedRelationship[]): void {
        const tbody = document.getElementById('relationships-table-body');
        if (!tbody) {
            console.error('Relationships table body not found');
            return;
        }

        tbody.innerHTML = '';

        if (relationships.length === 0) {
            tbody.innerHTML = '<tr><td colspan="7" class="text-center py-4 text-gray-500">No relationships found</td></tr>';
            return;
        }

        relationships.forEach(rel => {
            const row = document.createElement('tr');
            row.className = 'border-t hover:bg-gray-50';

            // Format relationship type with color
            const typeClass = rel.relationship_type === 'routes_to' ? 'bg-blue-100 text-blue-800' :
                             rel.relationship_type === 'connects_to' ? 'bg-green-100 text-green-800' :
                             rel.relationship_type === 'depends_on' ? 'bg-yellow-100 text-yellow-800' :
                             'bg-gray-100 text-gray-800';

            // Format date
            const createdDate = rel.created_at ?
                new Date(Number(rel.created_at) / 1000000).toLocaleDateString() : 'N/A';

            // Get enriched values (already parsed from metadata)
            const sourceTier = rel.source_tier || '-';
            const targetTier = rel.target_tier || '-';
            const serviceName = rel.service_name || '-';

            // Get application info
            const appName = rel.app_name || rel.application_id || 'Unknown';
            const appType = rel.app_type || '-';
            const appStatus = rel.app_status || 'unknown';
            const statusClass = this.getStatusClass(appStatus);

            row.innerHTML = `
                <td class="py-3 px-4">
                    <div>
                        <span class="font-medium">${this.escapeHtml(rel.device_id || 'Unknown')}</span>
                        <p class="text-xs text-gray-500">${this.escapeHtml(sourceTier)}</p>
                    </div>
                </td>
                <td class="py-3 px-4">
                    <span class="px-2 py-1 text-xs font-semibold rounded-full ${typeClass}">
                        ${this.escapeHtml(rel.relationship_type || 'relates_to')}
                    </span>
                </td>
                <td class="py-3 px-4">
                    <div>
                        <span class="font-medium">${this.escapeHtml(appName)}</span>
                        <p class="text-xs text-gray-500">${this.escapeHtml(appType)} - ${this.escapeHtml(targetTier)}</p>
                    </div>
                </td>
                <td class="py-3 px-4">${this.escapeHtml(serviceName)}</td>
                <td class="py-3 px-4">
                    <span class="px-2 py-1 text-xs font-semibold rounded-full ${statusClass}">
                        ${this.escapeHtml(appStatus)}
                    </span>
                </td>
                <td class="py-3 px-4">${createdDate}</td>
                <td class="py-3 px-4 text-center">
                    <button class="text-blue-600 hover:text-blue-800 text-sm" onclick="viewRelationship('${rel.relationship_id}')">
                        View
                    </button>
                </td>
            `;

            tbody.appendChild(row);
        });

        console.log(`Populated relationships table with ${relationships.length} rows`);
        this.updatePaginationInfo(relationships.length);
    }

    /**
     * Get all relationships (for external access)
     */
    public getAllRelationships(): EnrichedRelationship[] {
        return this.allRelationships;
    }

    /**
     * Get applications map (for external access by viewRelationship function)
     */
    public getApplicationsMap(): Map<string, Record<string, string>> {
        return this.applicationsMap;
    }
}
